import React, { useEffect, useState } from 'react';
import { isDevelopmentTimer } from '../utils/timerMode';
import { getEnrollmentLogs } from '../api/students';
import { StatusLog } from '../types/student';
import '../styles/EnrollmentLogsModal.css';

interface Props {
    isOpen: boolean;
    studentId: string;
    enrollmentId: string;
    courseName: string;
    onClose: () => void;
}

function statusBadge(status: string) {
    if (status.toLowerCase().includes('banned')) return 'banned';
    if (status.toLowerCase().includes('grant')) return 'granted';
    if (status.toLowerCase().includes('payment')) return 'payment';
    return '';
}

function triggerIcon(trigger: string) {
    if (trigger.toLowerCase().includes('ban')) return <div className="trigger-icon ban">!</div>;
    if (trigger.toLowerCase().includes('payment')) return <div className="trigger-icon payment">$</div>;
    return <div className="trigger-icon">{trigger.charAt(0).toUpperCase()}</div>;
}

export default function EnrollmentLogsModal({
                                                isOpen,
                                                studentId,
                                                enrollmentId,
                                                courseName,
                                                onClose
                                            }: Props) {
    const [logs, setLogs] = useState<StatusLog[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!isOpen) return;
        setLoading(true);
        setError(null);
        getEnrollmentLogs(studentId, enrollmentId)
            .then(setLogs)
            .catch(err => {
                console.error(err);
                setError('Failed to load logs.');
            })
            .finally(() => setLoading(false));
    }, [isOpen, studentId, enrollmentId]);

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="container enrollment-modal">
                <div className="header-student-modal">
                    <h1>{courseName}</h1>
                    <p>Access Status History</p>
                    <button className="enrollment-close-btn" onClick={onClose}>×</button>
                </div>
                <div className="timeline">
                    {loading && <p style={{ padding: '40px 0', textAlign: 'center' }}>Loading…</p>}
                    {error && <p className="modal__error" style={{ padding: '40px 0', textAlign: 'center', color: 'red' }}>{error}</p>}
                    {!loading && !error && logs.length === 0 && (
                        <p style={{ padding: '40px 0', textAlign: 'center' }}>No history yet.</p>
                    )}
                    {!loading && !error && logs.length > 0 && (
                        <>
                            {logs.map((l, i) => (
                                <div className={`timeline-item ${statusBadge(l.newStatus)}`} key={i}>
                                    <div className="event-card">
                                        <div className="event-header">
                                            <div className="event-status">
                                                {triggerIcon(l.triggerType)}
                                                <div className={`status-badge ${statusBadge(l.triggerType) || statusBadge(l.newStatus)}`}>
                                                    {l.triggerType.replace('_', ' ')}
                                                </div>
                                            </div>
                                            <div className="event-time">
                                                {new Date(l.changedAt).toLocaleString(undefined, {
                                                    dateStyle: 'medium',
                                                    timeStyle: 'short'
                                                })}
                                            </div>
                                        </div>
                                        <div className="status-transition">
                                            <span>{l.previousStatus || '—'}</span>
                                            <span className="status-arrow">→</span>
                                            <span>{l.newStatus}</span>
                                        </div>
                                        <div className="event-details">
                                            <div className="detail-item">
                                                <div className="detail-label">Trigger</div>
                                                <div className="detail-value">{l.triggerType.replace('_', ' ')}</div>
                                            </div>
                                            <div className="detail-item">
                                                <div className="detail-label">Grace Period</div>
                                                <div className="detail-value">
                                                    {l.gracePeriodDays
                                                        ? `${l.gracePeriodDays} ${isDevelopmentTimer ? 'minute' : 'day'}${l.gracePeriodDays > 1 ? 's' : ''}`
                                                        : 'None'}
                                                </div>
                                            </div>
                                            <div className="detail-item" style={{ gridColumn: "span 2" }}>
                                                <div className="detail-label">Access Window</div>
                                                <div className="access-period">
                                                    <div className="access-dates">
                                                        {l.channelAccessStart
                                                            ? `${new Date(l.channelAccessStart).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })} - ${l.channelAccessEnd
                                                                ? new Date(l.channelAccessEnd).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
                                                                : '—'}`
                                                            : '—'
                                                        }
                                                    </div>
                                                    <div className="access-duration">
                                                        {l.channelAccessStart && l.channelAccessEnd
                                                            ? (() => {
                                                                const start = new Date(l.channelAccessStart);
                                                                const end = new Date(l.channelAccessEnd);
                                                                const msInMonth = 30.44 * 24 * 3600 * 1000;
                                                                const months = Math.max(Math.round((end.getTime() - start.getTime()) / msInMonth), 1);
                                                                return `${months} month${months > 1 ? 's' : ''} access`;
                                                            })()
                                                            : '—'
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
